/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { ICtc } from "../../interfaces/CtcInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { getCtcs, postCtc, patchCtc, deleteCtc, calculateCtcSummary } from "../../api/ctc";

export const ctcsQueryKey = "ctcs";

// use ctcs
export const useCtcs = (filter?: {
  employeeIds?: number[];
  serviceProviderIds?: number[];
}): UseQueryResult<Array<ICtc>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [ctcsQueryKey, resourceCentre?.id, filter],
    queryFn: async () => {
      const response = await getCtcs(filter || {});
      return response.data || [];
    }
  });
};

// use post ctc
export const usePostCtc = (): UseMutationResult<
  ApiResponse<ICtc>,
  Error,
  Partial<ICtc>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ICtc>) => postCtc(data as any),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [ctcsQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "CTC created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create CTC",
          variant: "error"
        })
      );
    }
  });
};

// use patch ctc
export const usePatchCtc = (): UseMutationResult<
  ApiResponse<ICtc>,
  Error,
  { id: number | string; data: Partial<ICtc> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => patchCtc({ id, data: data as any }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [ctcsQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "CTC updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || error?.data?.message || "Failed to update CTC",
          variant: "error"
        })
      );
    }
  });
};

// use delete ctc
export const useDeleteCtc = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteCtc(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [ctcsQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "CTC deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete CTC",
          variant: "error"
        })
      );
    }
  });
};

// use calculate ctc
export const useCalculateCtcSummary = (): UseMutationResult<
  ApiResponse<any>,
  Error,
  Partial<ICtc>,
  unknown
> => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (data: Partial<ICtc>) => calculateCtcSummary(data),
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to calculate CTC",
          variant: "error"
        })
      );
    }
  });
};
